import { useState, useEffect } from 'react';
import { callsAPI } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  PhoneCall, PhoneIncoming, PhoneOutgoing, Search,
  Clock, Loader2, FileText, X, PlayCircle
} from 'lucide-react';

export default function CallHistoryPage() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCall, setSelectedCall] = useState(null);
  const [directionFilter, setDirectionFilter] = useState('all'); // all | outbound | inbound

  useEffect(() => {
    callsAPI.list()
      .then(res => setCalls(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-emerald-600/20 text-emerald-400 border-emerald-600/30',
      no_answer: 'bg-amber-600/20 text-amber-400 border-amber-600/30',
      busy: 'bg-orange-600/20 text-orange-400 border-orange-600/30',
      failed: 'bg-red-600/20 text-red-400 border-red-600/30',
      voicemail: 'bg-blue-600/20 text-blue-400 border-blue-600/30'
    };
    return styles[status] || styles.failed;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return dateStr; }
  };

  const filtered = calls.filter(c => {
    if (directionFilter !== 'all' && (c.direction || '').toLowerCase() !== directionFilter) return false;
    return (
      c.contact_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.phone_number?.includes(search) ||
      c.agent_name?.toLowerCase().includes(search.toLowerCase())
    );
  });

  const counts = {
    all: calls.length,
    outbound: calls.filter(c => (c.direction || '').toLowerCase() === 'outbound').length,
    inbound: calls.filter(c => (c.direction || '').toLowerCase() === 'inbound').length,
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-amber-500 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 max-w-7xl" data-testid="calls-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit'] text-white">Call History</h1>
          <p className="text-slate-400 mt-1">{calls.length} total calls</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search calls..."
            data-testid="calls-search-input"
            className="pl-10 bg-slate-950 border-white/10 text-white placeholder:text-slate-600"
          />
        </div>
      </div>

      <div className="flex gap-2 border-b border-white/5" data-testid="calls-direction-tabs">
        {[
          { id: 'all', label: 'All' },
          { id: 'outbound', label: 'Outbound' },
          { id: 'inbound', label: 'Inbound' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setDirectionFilter(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              directionFilter === t.id
                ? 'text-amber-400 border-amber-500'
                : 'text-slate-400 border-transparent hover:text-slate-200'
            }`}
          >
            {t.label}
            <span className="ml-2 text-xs text-slate-500">({counts[t.id]})</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="glass-card rounded-xl p-12 text-center" data-testid="no-calls">
          <PhoneCall className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold font-['Outfit'] text-white mb-2">No calls yet</h3>
          <p className="text-slate-400">Call history will appear here once your agents make or receive calls</p>
        </div>
      ) : (
        <div className="glass-card rounded-xl overflow-hidden" data-testid="calls-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">Contact</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">Agent</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">Direction</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">Duration</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">Date</th>
                  <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((call, i) => (
                  <tr key={call.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors animate-fade-in" style={{ animationDelay: `${i * 30}ms` }}
                    data-testid={`call-row-${call.id}`}>
                    <td className="px-5 py-3.5">
                      <div className="text-sm text-white font-medium">{call.contact_name}</div>
                      <div className="text-xs text-slate-500 font-mono">{call.phone_number}</div>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-300">{call.agent_name}</td>
                    <td className="px-5 py-3.5">
                      <span className="flex items-center gap-1.5 text-sm text-slate-400">
                        {call.direction === 'outbound' ? <PhoneOutgoing className="w-3.5 h-3.5" /> : <PhoneIncoming className="w-3.5 h-3.5" />}
                        {call.direction}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <Badge className={getStatusBadge(call.status)} data-testid={`call-status-${call.id}`}>
                        {call.status?.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="flex items-center gap-1.5 text-sm text-slate-400">
                        <Clock className="w-3 h-3" /> {formatDuration(call.duration)}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-400">{formatDate(call.created_at)}</td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {call.recording_url && (
                          <Button size="sm" variant="ghost" onClick={() => setSelectedCall({ ...call, showRecording: true })}
                            className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 text-xs"
                            data-testid={`play-recording-${call.id}`}>
                            <PlayCircle className="w-3.5 h-3.5 mr-1" /> Recording
                          </Button>
                        )}
                        {call.transcript && (
                          <Button size="sm" variant="ghost" onClick={() => setSelectedCall(call)}
                            className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 text-xs"
                            data-testid={`view-transcript-${call.id}`}>
                            <FileText className="w-3.5 h-3.5 mr-1" /> Transcript
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Transcript Dialog */}
      <Dialog open={!!selectedCall} onOpenChange={(open) => { if (!open) setSelectedCall(null); }}>
        <DialogContent className="max-w-lg bg-[#0B1221] border-white/10 text-white" data-testid="transcript-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              {selectedCall?.showRecording ? 'Call Recording' : 'Call Transcript'}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-4 text-sm text-slate-400">
              <span>{selectedCall?.contact_name}</span>
              <span>{selectedCall?.phone_number}</span>
              <Badge className={getStatusBadge(selectedCall?.status || '')}>{selectedCall?.status}</Badge>
            </div>
            {selectedCall?.showRecording && selectedCall?.recording_url && (
              <div className="bg-slate-950 rounded-lg p-4" data-testid="recording-player">
                <audio controls className="w-full" src={selectedCall.recording_url} data-testid="audio-player">
                  Your browser does not support the audio element.
                </audio>
                <p className="text-xs text-slate-500 mt-2">Recording duration: {formatDuration(selectedCall.recording_duration || selectedCall.duration)}</p>
              </div>
            )}
            {!selectedCall?.showRecording && (
              <div className="bg-slate-950 rounded-lg p-4 font-mono text-sm text-slate-300 whitespace-pre-wrap max-h-64 overflow-y-auto" data-testid="transcript-content">
                {selectedCall?.transcript || 'No transcript available'}
              </div>
            )}
            {selectedCall?.sentiment && (
              <div className="text-sm text-slate-400">
                Sentiment: <span className={`font-medium ${
                  selectedCall.sentiment === 'positive' ? 'text-emerald-400' :
                  selectedCall.sentiment === 'negative' ? 'text-red-400' : 'text-slate-300'
                }`}>{selectedCall.sentiment}</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
